// Imports
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { navigate, reload } from 'vike/client/router';

import { AuthShell, authLinkClass, authPrimaryButtonClass } from '@/components/Auth/AuthShell';
import FormAlert from '@/components/Forms/FormAlert';
import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import PasswordInput from '@/components/Forms/PasswordInput';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import Redirect from '@/components/Redirect';
import { Button } from '@/components/UI/Button';
import { useIsClient } from '@/renderer/utils/hooks/useIsClient';
import { MixpanelProxy, mixpanelTrack } from '@/renderer/utils/stat/mixp';
import useMe, { authToken } from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

// export const title = ({ urlOriginal }: Vike.PageContext) => urlOriginal === '/signin' ? 'Login' : 'Please sign in to access this page';

// Types
type LoginData = { username: string; password: string };

// Bound the login request so a hung fetch (e.g. a native app resumed before the
// network is ready) can't leave the submit button stuck disabled forever. On
// timeout the request is aborted, which clears `request.loading` and re-enables
// the button with a retryable message instead of a dead "please wait" state.
const LOGIN_REQUEST_TIMEOUT_MS = 20000;

// API requests
const authLoginRequest: RequestSchema<API.LoginState, { username: string; password: string }> = {
  name: 'authLoginRequest',
  input: '/api/v1/auth/login',
  method: 'POST',
};

export function LoginForm({
  guarded = false,
  subtitle,
  hideLogo = false,
}: {
  guarded: boolean;
  subtitle?: (React.ReactNode | JSX.Element)[] | React.ReactNode | JSX.Element | string;
  hideLogo?: boolean;
}) {
  // Form hooks
  const form = useForm<WithFormErrors<LoginData>>();

  const { abortReason, urlParsed, urlOriginal } = usePageContext();
  const [me, setMe] = useMe(true);
  const redirectTimeout = useRef<NodeJS.Timeout>();
  const IS_CLIENT = useIsClient();

  // Drive the button's "signed in, redirecting" state off an explicit success
  // flag rather than react-hook-form's `isSubmitSuccessful`. `useFormSubmit`
  // swallows request errors (it sets them as form errors and never re-throws), so
  // `isSubmitSuccessful` flips true even on a FAILED login — which used to leave
  // the button stuck showing "Successfully logged in, please wait…". This flag is
  // only ever set on a genuine success, and stays set through the navigation that
  // follows.
  const [redirecting, setRedirecting] = useState(false);

  // Request hooks
  const request = useRequest(authLoginRequest, { manual: true, hideAlert: true });

  const redirUrl = urlParsed?.search?.redirect?.[0] === '/' && urlParsed.search.redirect;

  // Handlers
  const handleFormSubmit = useFormSubmit(
    {
      form: form,
      callback: async (body) => {
        // Abort the request if it hangs (network not ready right after a native
        // resume). Aborting rejects `request.run`, which clears its loading state
        // and re-enables the button rather than leaving it stuck disabled.
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), LOGIN_REQUEST_TIMEOUT_MS);

        let result: Awaited<ReturnType<typeof request.run>>;
        try {
          result = await request.run(body, {
            throwOnRun: true,
            req: { init: { signal: controller.signal } },
          });
        } catch (error) {
          if (controller.signal.aborted) {
            // Replace the raw AbortError that useRequest stored so it doesn't
            // surface as a cryptic message, then throw a friendly, retryable one
            // for useFormSubmit to display.
            request.dispatch({ action: 'SET', values: { error: null } });
            throw new Error('Sign-in timed out. Check your connection and try again.');
          }

          throw error;
        } finally {
          clearTimeout(timeout);
        }

        if (result?.message === 'API.AUTHENTICATION.LOGIN.SUCCESS') {
          setRedirecting(true);

          if (result.user) {
            setMe(result.user);

            // Identify user in Mixpanel
            try {
              MixpanelProxy.identify(result.user.id.toString());
              mixpanelTrack('auth/signin');
            } catch (error) {
              console.warn('Failed to identify user in Mixpanel:', error);
            }
          }

          if (result.session?.token) {
            authToken(result.session.token);
          }

          if (redirUrl) {
            navigate(redirUrl);
          } else if (urlOriginal === '/signin') {
            navigate('/release-center');
          } else {
            if (IS_CLIENT) {
              // Just in case we get bugged and the page never changes, force a refresh of the current URL
              redirectTimeout.current = setTimeout(() => window.location.reload(), 10000);
            }

            reload();
          }
        }
      },
    },
    [redirUrl],
  );

  useEffect(() => {
    return () => {
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    };
  }, []);

  const registerUrl = useMemo(() => {
    if (urlParsed.search.redirect && urlParsed.search.redirect[0] === '/') {
      return `/register?redirect_uri=${encodeURIComponent(urlParsed.search.redirect)}`;
    } else if (urlOriginal !== '/signin') {
      return `/register?redirect_uri=${encodeURIComponent(urlOriginal)}`;
    }

    return '/register';
  }, [urlParsed.search.redirect, urlOriginal]);

  if (me) {
    return <Redirect url={redirUrl || '/release-center'} sideEffects={false} />;
  }

  // Any functional status/subtitle messaging that must not be dropped (guarded
  // flows, session-abort reasons, or an explicit subtitle passed by the caller).
  const statusMessage =
    (abortReason as string | undefined) ||
    (guarded && !subtitle ? 'Please sign in to access this page.' : undefined);

  // Render
  return (
    <AuthShell hideLogo={hideLogo}>
      {/* Card header */}
      <div className="flex flex-col items-center text-center">
        <h3 className="font-extrabold text-2xl text-zinc-900 tracking-tight dark:text-white">
          Welcome back
        </h3>
        {!!statusMessage && (
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{statusMessage}</p>
        )}
        {!!subtitle && (
          <div className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{subtitle}</div>
        )}
      </div>

      <form onSubmit={handleFormSubmit} method="POST" className="mt-7 space-y-5">
        <FormControl
          form={form}
          name="username"
          label="Email or username"
          autoComplete="email"
          required
          autoFocus
          autoCapitalize="off"
          autoCorrect="off"
          autoSave="off"
        />

        <FormControl
          form={form}
          name="password"
          label={
            <span className="flex w-full items-center justify-between">
              <span>Password</span>
              <a
                href="/forgot-password"
                className="font-medium text-[#15734A] text-xs hover:text-[#125f3d] dark:text-[#34B473] dark:hover:text-[#43c983]"
              >
                Forgot?
              </a>
            </span>
          }
          aria-autocomplete="list"
          autoComplete="password"
          wrap={PasswordInput}
          required
          minLength={8}
          autoCapitalize="off"
          autoCorrect="off"
          autoSave="off"
        />

        <FormErrors
          form={form}
          request={request}
          rewrite={(err) => (
            <FormAlert>
              {err.code === 404 ? (
                <>
                  Incorrect email/username or password.{' '}
                  <a
                    href="/forgot-password"
                    className="font-semibold text-red-700 underline underline-offset-2 hover:text-red-900 dark:text-red-200 dark:hover:text-red-100"
                  >
                    Reset password?
                  </a>
                </>
              ) : (
                err.toString()
              )}
            </FormAlert>
          )}
        />

        <FormSubmitButton
          disabled={request.loading || redirecting}
          loading={request.loading || redirecting}
          className={authPrimaryButtonClass}
        >
          {(redirecting && 'Successfully logged in, please wait...') || 'Sign in'}
        </FormSubmitButton>

        {!!guarded && (
          <div className="flex items-center justify-center">
            <Button variant="ghost" onClick={() => history.back()}>
              Or return to previous page <ArrowRightIcon height={16} className="size-4" />
            </Button>
          </div>
        )}
      </form>

      {/* Footer */}
      <div className="mt-6 border-zinc-950/10 border-t pt-5 text-center dark:border-white/10">
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          Not a member?{' '}
          <a href={registerUrl} className={`whitespace-nowrap ${authLinkClass}`}>
            Create an account →
          </a>
        </p>
      </div>
    </AuthShell>
  );
}
